// components/SearchByBatchModal.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Alert,
  Box,
  Chip,
  Tooltip,
  IconButton,
  TextField
} from '@mui/material';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface PricePrediction {
  id: string;
  machineId: number;
  machineModelName: string;
  machineHashrateTh: number;
  machinePower: number;
  batchReferenceId: string;
  btcAmountPaidToCustomer: number;
  btcAmountPaidToCustomerInUsd: number;
  hostingRevenueUsd: number;
  machinePriceUsd: number;
  daysMined: number;
  hostingCost: number;
  startDate: string;
  endDate: string;
  hkPrice: number;
  generatedBy: string;
  difficulty: number;
  btcPrice: number;
  timestamp: string;
}

interface GroupedData {
  id: string;
  machineId: number;
  machineModelName: string;
  machineHashrateTh: number;
  machinePower: number;
  batchReferenceId: string;
  hostingCosts: {
    [hostingCost: number]: {
      id: string;
      btcAmountPaidToCustomer: number;
      btcAmountPaidToCustomerInUsd: number;
      hostingRevenueUsd: number;
      machinePriceUsd: number;
      daysMined: number;
      startDate: string;
      endDate: string;
      hkPrice: number;
    }
  };
}

const SearchByBatchModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [predictions, setPredictions] = useState<PricePrediction[]>([]);
  const [groupedData, setGroupedData] = useState<GroupedData[]>([]);
  const [uniqueHostingCosts, setUniqueHostingCosts] = useState<number[]>([]);
  const [batchReferenceId, setBatchReferenceId] = useState<string>('');
  const [copied, setCopied] = useState(false);

  const formatDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const groupDataByMachineAndHostingCost = (data: PricePrediction[]) => {
    const grouped: { [key: string]: GroupedData } = {};
    const hostingCosts = new Set<number>();

    data.forEach(prediction => {
      hostingCosts.add(prediction.hostingCost);
      
      const key = `${prediction.machineId}-${prediction.machineModelName}`;
      
      if (!grouped[key]) {
        grouped[key] = {
          id: prediction.id,
          machineId: prediction.machineId,
          machineModelName: prediction.machineModelName,
          machineHashrateTh: prediction.machineHashrateTh,
          machinePower: prediction.machinePower,
          batchReferenceId: prediction.batchReferenceId,
          hostingCosts: {}
        };
      }
      
      grouped[key].hostingCosts[prediction.hostingCost] = {
        id: prediction.id,
        btcAmountPaidToCustomer: prediction.btcAmountPaidToCustomer,
        btcAmountPaidToCustomerInUsd: prediction.btcAmountPaidToCustomerInUsd,
        hostingRevenueUsd: prediction.hostingRevenueUsd,
        machinePriceUsd: prediction.machinePriceUsd,
        daysMined: prediction.daysMined,
        startDate: prediction.startDate,
        endDate: prediction.endDate,
        hkPrice: prediction.hkPrice
      };
    });

    setUniqueHostingCosts(Array.from(hostingCosts).sort());
    return Object.values(grouped);
  };

  const fetchBatchData = async (batchId: string) => {
    if (!batchId.trim()) {
      setError('Please enter a Batch Reference ID');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_PATH}/price-predictions/batch/${batchId}`, getAxiosConfig());
      setPredictions(response.data);
      setGroupedData(groupDataByMachineAndHostingCost(response.data));
    } catch (err) {
      console.error('Error fetching batch predictions:', err);
      setError('Failed to load batch data. Please check the Batch Reference ID.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchBatchData(batchReferenceId);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setBatchReferenceId('');
    setPredictions([]);
    setGroupedData([]);
    setError(null);
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatHostingCost = (cost: number): string => {
    return `$${cost.toFixed(2)}`;
  };

  // CSV Download function
  const downloadCSV = () => {
    if (groupedData.length === 0) return;

    // Create CSV headers matching the table structure
    const headers = [
      'Machine Model',
      'Batch Reference ID',
      'Machine Id',
      'Hashrate (Th)',
      'Power (W)',
      'HK Price',
      ...uniqueHostingCosts.flatMap(cost => [
        `Calculated Price (${formatHostingCost(cost)})`,
        `Selling Price (${formatHostingCost(cost)})`
      ])
    ];

    // Create CSV data
    const csvData = groupedData.map(machine => {
      const row = [
        `"${machine.machineModelName}"`,
        machine.batchReferenceId || '',
        machine.id || '',
        machine.machineHashrateTh,
        machine.machinePower,
        (() => {
          const firstHostingCost = uniqueHostingCosts[0];
          const firstData = machine.hostingCosts[firstHostingCost];
          return firstData ? (firstData.hkPrice || 0) : 0;
        })()
      ];

      // Add data for each hosting cost - matching table columns
      uniqueHostingCosts.forEach(hostingCost => {
        const data = machine.hostingCosts[hostingCost];
        if (data) {
          const hkPrice = data.hkPrice || 0;
          const calculatedPrice = data.machinePriceUsd || 0;
          const sellingPrice = Math.max(hkPrice, calculatedPrice);
          
          row.push(
            calculatedPrice,          // Calculated Price
            sellingPrice       // Selling Price
          );
        } else {
          // Add empty values if no data for this hosting cost
          row.push('', '', '');
        }
      });

      return row;
    });

    // Combine headers and data
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `batch_prices_${batchReferenceId || 'data'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyToClipboard = (text: string) => {
    if (!text) return;
    
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  function getLastUuidSegment(id: string): string {
    if (!id) return "";
    const parts = id.split("-");
    return parts[parts.length - 1]; 
  }

  return (
    <>
      <div style={{ 
        width: '100%', 
        marginBottom: '16px', 
        padding: '16px', 
        border: '1px solid #ccc', 
        borderRadius: '4px', 
        backgroundColor: '#f9f9f9',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        minHeight: '60px'
      }}>
        <Typography variant="h6">
          Search Historical Data
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ margin: 0, flex: 1 }}>
          Search price predictions by Batch Reference ID to view historical data.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleOpen}
          style={{ flexShrink: 0 }}
        >
          Search by Batch Reference
        </Button>
      </div>

      {/* Modal */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          style: { minHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" component="div" fontWeight="bold">
                Search by Batch Reference
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Enter Batch Reference ID to view historical price predictions
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadCSV}
              disabled={groupedData.length === 0}
              size="small"
            >
              Download CSV
            </Button>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {/* Search Input Section */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Enter Batch Reference ID
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter Batch Reference ID..."
                value={batchReferenceId}
                onChange={(e) => setBatchReferenceId(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                disabled={loading}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading || !batchReferenceId.trim()}
                sx={{ minWidth: 100 }}
              >
                {loading ? <CircularProgress size={20} /> : 'Search'}
              </Button>
            </Box>
          </Box>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <CircularProgress />
            </div>
          ) : error ? (
            <Alert severity="error" style={{ marginBottom: '16px' }}>
              {error}
            </Alert>
          ) : groupedData.length === 0 && batchReferenceId ? (
            <Alert severity="info" style={{ marginBottom: '16px' }}>
              No data found for this Batch Reference ID.
            </Alert>
          ) : groupedData.length === 0 ? (
            <Alert severity="info" style={{ marginBottom: '16px' }}>
              Enter a Batch Reference ID and click Search to view data.
            </Alert>
          ) : (
            <TableContainer component={Paper} style={{ maxHeight: '60vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 200 }}><Typography variant="subtitle2" fontWeight="bold">Machine Model</Typography></TableCell>
                    <TableCell align="right" sx={{ minWidth: 120 }}><Typography variant="subtitle2" fontWeight="bold">Machine Ref</Typography></TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}><Typography variant="subtitle2" fontWeight="bold">Hashrate (Th)</Typography></TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}><Typography variant="subtitle2" fontWeight="bold">Power (W)</Typography></TableCell>
                    <TableCell align="right" sx={{ minWidth: 120, backgroundColor: '#f1f1f1ff' }}><Typography variant="subtitle2" fontWeight="bold">HK Price</Typography></TableCell>
                    {uniqueHostingCosts.map((hostingCost, index) => (
                      <TableCell 
                        key={hostingCost} 
                        colSpan={3} 
                        align="center" 
                        sx={{ 
                          backgroundColor: index % 3 === 0 ? '#e3f2fd' : 
                                          index % 3 === 1 ? '#f3e5f5' : 
                                          '#e8f5e8',
                          borderLeft: '2px solid white',
                          borderRight: '2px solid white',
                          minWidth: 350
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="bold">
                          Hosting: {formatHostingCost(hostingCost)}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#fafafa' }}></TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#fafafa' }}></TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#fafafa' }}></TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#fafafa' }}></TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#f1f1f1ff' }}></TableCell>
                    {uniqueHostingCosts.map((hostingCost, index) => (
                      <React.Fragment key={hostingCost}>
                        <TableCell 
                          align="center"
                          sx={{ 
                            backgroundColor: index % 3 === 0 ? '#e3f2fd' : 
                                            index % 3 === 1 ? '#f3e5f5' : 
                                            '#e8f5e8'
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold">SG Price</Typography>
                        </TableCell>
                        <TableCell 
                          align="center"
                          sx={{ 
                            backgroundColor: index % 3 === 0 ? '#e3f2fd' : 
                                            index % 3 === 1 ? '#f3e5f5' : 
                                            '#e8f5e8'
                          }}
                        >
                          <Box sx={{ textAlign: 'center', p: 1 }}>
                            <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                              Price Difference
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ 
                              fontSize: '0.7rem',
                              opacity: 0.8,
                              letterSpacing: 0.2
                            }}>
                              (HK Price - SG Price)
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell 
                          align="center"
                          sx={{ 
                            backgroundColor: index % 3 === 0 ? '#e3f2fd' : 
                                            index % 3 === 1 ? '#f3e5f5' : 
                                            '#e8f5e8',
                            borderRight: '2px solid white'
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold">Selling Price</Typography>
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedData.map((machine, rowIndex) => (
                    <TableRow 
                      key={`${machine.machineId}-${machine.machineModelName}`} 
                      hover
                      sx={{ 
                        backgroundColor: rowIndex % 2 === 0 ? '#fafafa' : 'white'
                      }}
                    >
                      <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                        <div>
                          <Typography variant="body2" fontWeight="medium">
                            {machine.machineModelName}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell align="right" sx={{ borderRight: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <Typography variant="body2">
                            {getLastUuidSegment(machine.id) || '-'}
                          </Typography>
                          {getLastUuidSegment(machine.id) && (
                            <Tooltip title="Copy to clipboard">
                              <IconButton 
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(machine.id);
                                }}
                                sx={{ 
                                  p: 0.5,
                                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ borderRight: '1px solid #e0e0e0' }}>
                        <Typography variant="body2">
                          {machine.machineHashrateTh?.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ borderRight: '1px solid #e0e0e0' }}>
                        <Typography variant="body2">
                          {machine.machinePower?.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ borderRight: '1px solid #e0e0e0', backgroundColor: '#f2f2f2' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {(() => {
                            const firstHostingCost = uniqueHostingCosts[0];
                            const firstData = machine.hostingCosts[firstHostingCost];
                            return firstData ? formatCurrency(firstData.hkPrice || 0) : '-';
                          })()}
                        </Typography>
                      </TableCell>
                      {uniqueHostingCosts.map((hostingCost, colIndex) => {
                        const data = machine.hostingCosts[hostingCost];
                        const bgColor = colIndex % 3 === 0 ? '#f3f9ff' : 
                                    colIndex % 3 === 1 ? '#faf5ff' : 
                                    '#f8fff8';
                        
                        if (data) {
                          const hkPrice = data.hkPrice || 0;
                          const sgPrice = data.machinePriceUsd || 0;
                          const difference = hkPrice - sgPrice;
                          const differenceColor = difference >= 0 ? '#4caf50' : '#f44336';
                          const sellingPrice = Math.max(hkPrice, sgPrice);
                          
                          return (
                            <React.Fragment key={`${machine.machineId}-${hostingCost}`}>
                              <TableCell align="center" sx={{ backgroundColor: bgColor }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {formatCurrency(sgPrice)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ backgroundColor: bgColor }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight="bold"
                                  sx={{ color: differenceColor }}
                                >
                                  {formatCurrency(difference)}
                                </Typography>
                              </TableCell>
                              <TableCell 
                                align="center" 
                                sx={{ 
                                  backgroundColor: bgColor,
                                  borderRight: '2px solid white'
                                }}
                              >
                                <Typography 
                                  variant="body2" 
                                  fontWeight="bold"
                                  sx={{ 
                                    color: sellingPrice === hkPrice ? '#ff9800' : '#2196f3',
                                    backgroundColor: sellingPrice === hkPrice ? '#fff3e0' : '#e3f2fd',
                                    padding: '4px 8px',
                                    borderRadius: '4px'
                                  }}
                                >
                                  {formatCurrency(sellingPrice)}
                                  {sellingPrice === hkPrice && (
                                    <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: '#ff9800' }}>
                                      (HK Price)
                                    </Typography>
                                  )}
                                  {sellingPrice === sgPrice && (
                                    <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: '#2196f3' }}>
                                      (SG Price)
                                    </Typography>
                                  )}
                                </Typography>
                              </TableCell>
                            </React.Fragment>
                          );
                        } else {
                          return (
                            <React.Fragment key={`${machine.machineId}-${hostingCost}`}>
                              <TableCell align="center" sx={{ backgroundColor: bgColor }}>
                                <Typography variant="body2" color="textSecondary">-</Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ backgroundColor: bgColor }}>
                                <Typography variant="body2" color="textSecondary">-</Typography>
                              </TableCell>
                              <TableCell 
                                align="center" 
                                sx={{ 
                                  backgroundColor: bgColor,
                                  borderRight: '2px solid white'
                                }}
                              >
                                <Typography variant="body2" color="textSecondary">-</Typography>
                              </TableCell>
                            </React.Fragment>
                          );
                        }
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {/* Additional Info Section */}
          {predictions.length > 0 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Batch Information
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block">BTC Price</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {predictions[0]?.btcPrice ? formatCurrency(predictions[0].btcPrice) : 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block">Difficulty</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {predictions[0]?.difficulty?.toLocaleString() || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block">Generated By</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {predictions[0]?.generatedBy || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Batch Reference ID
                    </Typography>
                    <Typography variant="body2" fontWeight="Bold">
                      {predictions[0]?.batchReferenceId || 'N/A'}
                    </Typography>
                  </Box>
                  <Tooltip title="Copy to clipboard">
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(predictions[0]?.batchReferenceId || '')}
                      disabled={!predictions[0]?.batchReferenceId}
                      sx={{ 
                        alignSelf: 'flex-start',
                        mt: 0.5,
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block">Timestamp</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {predictions[0]?.timestamp || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block">Hosting Costs</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {uniqueHostingCosts.map((cost, index) => (
                      <Chip 
                        key={cost} 
                        label={formatHostingCost(cost)} 
                        size="small" 
                        variant="filled"
                        sx={{
                          backgroundColor: index % 3 === 0 ? '#e3f2fd' : 
                                          index % 3 === 1 ? '#f3e5f5' : 
                                          '#e8f5e8',
                          color: 'text.primary',
                          fontSize: '0.7rem',
                          height: 24
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} color="primary" variant="outlined">
            Close
          </Button>
          <Button 
            onClick={() => fetchBatchData(batchReferenceId)} 
            color="primary" 
            variant="contained"
            disabled={loading || !batchReferenceId.trim()}
          >
            {loading ? <CircularProgress size={20} /> : 'Search Again'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SearchByBatchModal;